import {KycRecord} from "../../domain/kyc.entity";
import {
  CreateKycParams,
  UpdateKycParams,
  UploadKycDocumentsParams,
  UploadKycDocumentsResult,
  KycDocumentField,
} from "../../domain/kyc.types";
import {httpClientV2} from "../../../../infrastructure/http";
import {KycMapperV2} from "../mappers/kyc.mapper.v2";
import {
  V2GetKycResponse,
  V2CreateKycResponse,
  V2UploadDocumentsResponse,
  V2DocumentType,
} from "../mappers/kyc.mapper.types";
import {KycRepository} from "./kyc.repository";

/**
 * v2 API implementation of KycRepository.
 *
 * Pure TypeScript - no React imports.
 */
export class KycRepositoryV2 implements KycRepository {
  async getKycStatus(): Promise<KycRecord | null> {
    try {
      const response = await httpClientV2.get<V2GetKycResponse>("kyc");
      return KycMapperV2.fromGetResponse(response);
    } catch (error: unknown) {
      if (this.isNotFoundError(error)) {
        return null;
      }
      throw error;
    }
  }

  async createKycRecord(params: CreateKycParams): Promise<KycRecord> {
    const request = KycMapperV2.toCreateRequest(params);
    const response = await httpClientV2.post<V2CreateKycResponse>("kyc", request);
    return KycMapperV2.fromCreateResponse(response, params.userId);
  }

  async updateKycRecord(params: UpdateKycParams): Promise<KycRecord> {
    const request = KycMapperV2.toUpdateRequest(params);
    const response = await httpClientV2.patch<V2CreateKycResponse>("kyc", request);
    return KycMapperV2.fromCreateResponse(response, params.userId);
  }

  async uploadDocuments(
    params: UploadKycDocumentsParams
  ): Promise<UploadKycDocumentsResult> {
    const formData = new FormData();
    const documentTypes: V2DocumentType[] = [];

    // Map domain document field names to API document type names
    const fieldToType: Record<KycDocumentField, V2DocumentType> = {
      proofOfAddress: "proofOfAddress",
      governmentId: "governmentId",
      selfieWithId: "selfieWithId",
      businessRegistration: "businessRegistration",
    };

    // Add each file to FormData with its document type as key
    for (const [field, file] of Object.entries(params.files)) {
      if (file instanceof File) {
        const docType = fieldToType[field as KycDocumentField];
        formData.append(docType, file);
        documentTypes.push(docType);
      }
    }

    // API requires documentTypes as JSON array
    formData.append("documentTypes", JSON.stringify(documentTypes));

    const response = await httpClientV2.post<V2UploadDocumentsResponse>(
      "kyc/upload-documents",
      formData,
      {
        headers: {"Content-Type": "multipart/form-data"},
      }
    );

    return KycMapperV2.toUploadDocumentsResult(response);
  }

  async submitForReview(): Promise<void> {
    await httpClientV2.patch("kyc/submit-review");
  }

  /**
   * Checks if an error indicates no KYC record exists (404).
   * httpClientV2 rejects with response.data body: { status: false, message: "..." }
   */
  private isNotFoundError(error: unknown): boolean {
    if (!error || typeof error !== "object") return false;

    const msg = (error as {message?: unknown}).message;
    if (typeof msg === "string") {
      const lower = msg.toLowerCase();
      if (lower.includes("not found")) return true;
      if (lower.includes("no kyc")) return true;
    }

    return false;
  }
}
